import { AdyenCustomerType } from "../Customer";
import ACHLogo from "../../Assets/Finance/ACHLogo.svg";
import AmexLogo from "../../Assets/Finance/AmexLogo.svg";
import MasterCardLogo from "../../Assets/Finance/MasterCardLogo.svg";
import VisaLogo from "../../Assets/Finance/VisaLogo.svg";
import BacsLogo from "../../Assets/Finance/BacsLogo.svg";
import { PaymentMethodsConfiguration } from "@adyen/adyen-web/dist/types/core/types";
import { AdyenMandatedBy, Money } from "../AdyenSession";
import { Team } from "../User";
import { Invoice } from "../Invoice";
import { PaymentsIntegrationEnum, PaymentsIntegrationView } from "../Integration";
import { MultiGetResponse } from "../Common";

export interface Transaction {
  id: number;
  created_at: Date;
  updated_at: Date;
  customer_id?: number | null;
  customer_type: AdyenCustomerType;
  transactionable_id?: number | null;
  transactionable_type: AdyenTransactionableType;
  status: AdyenTransactionStatus;
  failed_reason: string;
  transaction_completed_date?: Date | null;
  total_amount: number;
  net_amount: number;
  commission_amount: number;
  transaction_fee: number;
  payment_method: AdyenPaymentMethodType;
  payment_method_summary: string;
  customer_name: string;
  transaction_identifier: string;
  psp_reference?: string;
}

export enum AdyenPaymentMethodType {
  Visa = "visa",
  Mastercard = "mc",
  Amex = "amex",
  Ach = "ach",
  Bacs = "bacs",
  Maestro = "maestro"
}

export enum AdyenTransactionableType {
  CollectibleInvoice = "App\\Domains\\Payments\\Models\\CollectibleInvoice",
  PaymentsGrowQuote = "App\\Domains\\Payments\\Models\\PaymentsGrowQuote"
}

export enum AdyenPaymentMethodVerificationStatus {
  Pending = "pending",
  Valid = "valid"
}

export enum AdyenTransactionStatus {
  Initiated = "Initiated",
  Completed = "Completed",
  Failed = "Failed",
  Processing = "Processing",
  Refunded = "Refunded",
  PartiallyRefunded = "Partially Refunded",
  Chargeback = "Chargeback",
  RefundInitiated = "Refund Initiated"
}

export interface AllowedPaymentMethod {
  customer_id: number;
  type: AdyenPaymentMethodType;
  enabled: boolean;
}

export interface GiactValidationRequest {
  billing_address_data: any;
  payment_method: any;
  customer_id?: number;
  session_uuid?: string;
  referrer: string;
}

export interface TransactionSplitData {
  amount?: number;
  reference: string;
}

export interface TransactionSplit {
  sale: TransactionSplitData;
  commission: TransactionSplitData;
  payment_fee: TransactionSplitData;
}

export interface TransactionDataRequest {
  reference: string;
  splits: TransactionSplit;
  total_amount: Money;
  surcharges_fee_amount: Money;
  original_amount: Money;
  expires_at: Date;
}

export interface AdyenTransactionCreateRequest {
  method: AdyenPaymentMethodType;
  invoice_id?: number;
  session_uuid?: string;
  transaction: TransactionDataRequest;
}

export enum GiactValidationResult {
  Passed = "Passed",
  Failed = "Failed",
  Started = "Started",
  Error = "Error",
  NoResult = "No Result"
}

export interface GiactValidationView {
  bank_verification_result: GiactValidationResult;
  error?: string;
}

export interface AdyenTransactionCreateView {
  reference: string;
  error?: string;
}

export interface AdyenStoredPaymentTokenForClientPortalView {
  id: number;
  type: AdyenPaymentMethodType;
  primary: boolean;
  customerId: number;
  summary: string;
  details: string;
  mandated_by: AdyenMandatedBy;
}

export interface ShowPaymentResultView {
  message: string;
  transaction: Transaction;
  team: Team;
  invoice: Invoice;
  pdf_url: string;
  is_success: boolean;
  is_direct_debit: boolean;
  settlement_days?: string;
}

export interface QuotePaymentResult {
  message: string;
  transaction: Transaction;
  team: Team;
  quoteId: number;
  growCustomerId: number;
  pdfUrl: string;
  isSuccess: boolean;
  isDirectDebit: boolean;
  settlementDays?: string;
}

export interface AdyenStoredPaymentMethod {
  id: number;
  team_id: number;
  customer_id: number;
  customer_type: AdyenCustomerType;
  original_shopper_reference: string;
  original_transaction_psp_reference: string;
  transaction_id: number;
  transaction_type: AdyenTransactionableType;
  payment_method_type: AdyenPaymentMethodType;
  primary: boolean;
  mandated_by: AdyenMandatedBy;
  mandate_pdf?: string;
  metadata?: any;
  created_at: Date;
  updated_at: Date;
}

const paymentMethodDetails = {
  [AdyenPaymentMethodType.Visa]: {
    name: "VISA",
    logo: VisaLogo,
    methodDropinConfiguration: {
      card: {
        hasHolderName: true,
        holderNameRequired: true,
        positionHolderNameOnTop: true,
        maskSecurityCode: true,
        name: "VISA",
        brands: ["visa"]
      }
    } as PaymentMethodsConfiguration
  },
  [AdyenPaymentMethodType.Mastercard]: {
    name: "Mastercard",
    logo: MasterCardLogo,
    methodDropinConfiguration: {
      card: {
        hasHolderName: true,
        holderNameRequired: true,
        positionHolderNameOnTop: true,
        maskSecurityCode: true,
        name: "Mastercard",
        brands: ["mc"]
      }
    } as PaymentMethodsConfiguration
  },
  [AdyenPaymentMethodType.Amex]: {
    name: "American Express",
    logo: AmexLogo,
    methodDropinConfiguration: {
      card: {
        hasHolderName: true,
        holderNameRequired: true,
        positionHolderNameOnTop: true,
        maskSecurityCode: true,
        name: "American Express",
        brands: ["amex"]
      }
    } as PaymentMethodsConfiguration
  },
  [AdyenPaymentMethodType.Ach]: {
    name: "ACH Transfer",
    logo: ACHLogo,
    methodDropinConfiguration: {} as PaymentMethodsConfiguration
  },
  [AdyenPaymentMethodType.Bacs]: {
    name: "Direct Debit",
    logo: BacsLogo,
    methodDropinConfiguration: {} as PaymentMethodsConfiguration
  },
  [AdyenPaymentMethodType.Maestro]: {
    name: "Maestro",
    logo: MasterCardLogo,
    methodDropinConfiguration: {} as PaymentMethodsConfiguration
  }
};

export function getPaymentMethodDetails(type: AdyenPaymentMethodType): {
  name: string;
  logo: string;
  type: AdyenPaymentMethodType;
  methodDropinConfiguration: PaymentMethodsConfiguration;
} {
  return paymentMethodDetails[type]
    ? { ...paymentMethodDetails[type], type }
    : {
        name: "Unknown",
        logo: "",
        type,
        methodDropinConfiguration: {} as PaymentMethodsConfiguration
      };
}

export interface Payout {
  id: number;
  created_at: Date;
  updated_at: Date;
  amount: number;
  status?: AdyenBalanceAccountTransferStatus;
  is_reconcile_required: boolean;
  is_contain_chargeback: boolean;
  payout_reconcile_display_error?: string;
}

export interface LinkItem {
  display_value: string;
  link: string;
}

export interface PayoutChargebackScreenMessage {
  article_link: string;
  connected_integration: PaymentsIntegrationEnum;
  is_chargeback_fee_include: boolean;
  is_payout_paused: boolean;
  invoices: LinkItem[];
  out_standing_amount: string;
  quotes: LinkItem[];
}

export interface PayoutScreenMessage {
  refund_ref?: string;
  pending_chargeback_transfers_count?: number;
  chargeback_message?: PayoutChargebackScreenMessage;
}

export interface PayoutIndex {
  payments_payouts: MultiGetResponse<Payout>;
  has_payout_capability: boolean;
  is_reviewing_kyc: boolean;
  available_balance?: number;
  payout_screen_message?: PayoutScreenMessage;
  current_integration?: PaymentsIntegrationView;
}

export interface PayoutTransferView {
  type: string;
  transactionable: string;
  transactionable_url?: string;
  transaction_reference: string;
  date: Date;
  original_amount: number;
  surcharge: number;
  fee: number;
  net: number;
}

export interface PayoutShowView {
  deposit_url?: string;
  deposit_link_text: string;
  total: number;
  transfers: PayoutTransferView[];
}

export enum AdyenBalanceAccountTransferStatus {
  Received = "received",
  Authorised = "authorised",
  Refused = "refused",
  Error = "error",
  Booked = "booked",
  Captured = "captured",
  Returned = "returned",
  Failed = "failed",
  Chargeback = "chargeback",
  Fee = "fee",
  Refunded = "refunded",
  CaptureReversed = "captureReversed",
  InvoiceDeduction = "invoiceDeduction",
  DepositCorrection = "depositCorrection"
}

export enum AdyenPaymentsResultCodes {
  Authorised = "Authorised",
  Error = "Error",
  Pending = "Pending",
  Refused = "Refused",
  Received = "Received"
}

export interface BacsResponse {
  success: boolean;
  status: AdyenPaymentsResultCodes;
}

export interface PayoutTroubleshoot {
  confirmations: string[];
  errors: string[];
  payoutId: number;
}

export interface RefundPayload {
  refund_reason: string;
}
